import { computed, toValue, type MaybeRefOrGetter } from "vue";

import { useQuery } from "@tanstack/vue-query";

import { SearchService } from "../services/search.service";

import type { SearchItem } from "@courses/types-layer/types/search";

export const useSearchQueryStore = () => {
  const service = new SearchService();

  // ===== QUERY KEY FACTORY =====
  const queryKeys = {
    all: ["search"] as const,
    items: (query: string) => ["search", "items", query] as const,
    count: (query: string) => ["search", "count", query] as const,
    productTitles: (productIds: string[]) =>
      ["search", "product-titles", productIds.sort().join(",")] as const,
  };

  // ===== QUERY CREATION FUNCTIONS =====

  const createSearchItemsQuery = (searchQuery: MaybeRefOrGetter<string>) => {
    return useQuery<SearchItem[], Error>({
      queryKey: computed(() => {
        const query = toValue(searchQuery);
        return queryKeys.items(query || "");
      }),
      queryFn: async () => {
        const query = toValue(searchQuery);
        if (!query || !query.trim()) {
          return [];
        }
        return await service.searchItems({
          searchKey: query,
          pageLimit: 30,
          pageNumber: 1,
        });
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      enabled: computed(() => {
        const query = toValue(searchQuery);
        return !!query && query.trim().length > 0;
      }),
      refetchOnWindowFocus: false,
    });
  };

  const createProductTitlesQuery = (productIds: MaybeRefOrGetter<string[]>) => {
    return useQuery<Record<string, string>, Error>({
      queryKey: computed(() => {
        const ids = toValue(productIds);
        return queryKeys.productTitles(ids);
      }),
      queryFn: async () => {
        const ids = toValue(productIds);
        if (!ids || ids.length === 0) {
          return {};
        }
        return await service.getProductTitles({ productIds: ids });
      },
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes
      enabled: computed(() => {
        const ids = toValue(productIds);
        return ids.length > 0;
      }),
      refetchOnWindowFocus: false,
    });
  };

  return {
    // Query creation functions
    createSearchItemsQuery,
    createProductTitlesQuery,

    // Query keys for manual invalidation
    queryKeys,
  };
};
